import { useState } from 'react'
import { Link, router } from '@inertiajs/react'
import {
  Plus,
  ArrowLeftRight,
  Pencil,
  Trash2,
  Download,
} from 'lucide-react'
import AppLayout from '@/layouts/AppLayout'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { formatCurrency, formatDate } from '@/lib/utils'
import Pagination from '@/components/Pagination'
import type { Adjustment, PaginationMeta } from '@/types'

interface Props {
  adjustments: Adjustment[]
  finalized_pos: { id: number; amount: string }[]
  filters: { type: string | null }
  pagination: PaginationMeta
}

const emptyForm = {
  amount: '',
  adjustment_type: 'credit' as 'credit' | 'debit',
  date: new Date().toISOString().split('T')[0],
  notes: '',
  purchase_order_id: '',
}

export default function AdjustmentsIndex({ adjustments, finalized_pos, filters, pagination }: Props) {
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [form, setForm] = useState(emptyForm)
  const [processing, setProcessing] = useState(false)

  function handleFilterChange(value: string) {
    router.get(
      '/adjustments',
      { type: value === 'all' ? null : value },
      { preserveState: true, preserveScroll: true }
    )
  }

  function openCreateDialog() {
    setEditingId(null)
    setForm(emptyForm)
    setDialogOpen(true)
  }

  function openEditDialog(adjustment: Adjustment) {
    setEditingId(adjustment.id)
    setForm({
      amount: adjustment.amount,
      adjustment_type: adjustment.adjustment_type,
      date: adjustment.date,
      notes: adjustment.notes || '',
      purchase_order_id: adjustment.purchase_order_id?.toString() || '',
    })
    setDialogOpen(true)
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setProcessing(true)

    const payload = {
      adjustment: {
        ...form,
        purchase_order_id: form.purchase_order_id || null,
      },
    }

    if (editingId) {
      router.put(`/adjustments/${editingId}`, payload, {
        onFinish: () => {
          setProcessing(false)
          setDialogOpen(false)
        },
      })
    } else {
      router.post('/adjustments', payload, {
        onFinish: () => {
          setProcessing(false)
          setDialogOpen(false)
        },
      })
    }
  }

  function handleDelete(id: number) {
    if (confirm('Are you sure you want to delete this adjustment?')) {
      router.delete(`/adjustments/${id}`)
    }
  }

  return (
    <AppLayout>
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-foreground">Adjustments</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Track credits and debits
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button asChild variant="outline" size="sm">
            <a href={`/adjustments/csv${filters.type ? `?type=${filters.type}` : ''}`} download>
              <Download className="size-4" />
              CSV
            </a>
          </Button>
          <Button size="sm" onClick={openCreateDialog}>
            <Plus className="size-4" />
            Add Adjustment
          </Button>
        </div>
      </div>

      {/* Filter bar */}
      <Card className="border-border/50 mb-6">
        <CardContent className="flex items-center gap-4 pt-6">
          <span className="text-sm font-medium text-muted-foreground">Filter:</span>
          <Select
            value={filters.type || 'all'}
            onValueChange={handleFilterChange}
          >
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="All adjustments" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Adjustments</SelectItem>
              <SelectItem value="credit">Credits</SelectItem>
              <SelectItem value="debit">Debits</SelectItem>
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {/* Data table */}
      <Card className="border-border/50">
        <CardContent className="pt-0">
          {adjustments.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
              <ArrowLeftRight className="size-10 mb-3 opacity-30" />
              <p className="text-sm font-medium">No adjustments found</p>
              <p className="text-xs mt-1">Add an adjustment to get started</p>
              <Button size="sm" className="mt-4" onClick={openCreateDialog}>
                <Plus className="size-4" />
                Add Adjustment
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-border/50 hover:bg-transparent">
                  <TableHead className="text-xs text-muted-foreground">Type</TableHead>
                  <TableHead className="text-xs text-muted-foreground">Amount</TableHead>
                  <TableHead className="text-xs text-muted-foreground">Date</TableHead>
                  <TableHead className="text-xs text-muted-foreground">Linked PO</TableHead>
                  <TableHead className="text-xs text-muted-foreground">Notes</TableHead>
                  <TableHead className="text-xs text-muted-foreground text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {adjustments.map((adjustment) => (
                  <TableRow key={adjustment.id} className="border-border/30">
                    <TableCell>
                      <span
                        className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                          adjustment.adjustment_type === 'credit'
                            ? 'bg-positive/10 text-positive'
                            : 'bg-destructive/10 text-destructive'
                        }`}
                      >
                        {adjustment.adjustment_type === 'credit' ? 'Credit' : 'Debit'}
                      </span>
                    </TableCell>
                    <TableCell className="font-mono text-sm font-medium">
                      {formatCurrency(parseFloat(adjustment.amount))}
                    </TableCell>
                    <TableCell className="text-sm">
                      {formatDate(adjustment.date)}
                    </TableCell>
                    <TableCell>
                      {adjustment.purchase_order_id ? (
                        <Link
                          href={`/purchase_orders/${adjustment.purchase_order_id}`}
                          className="text-primary hover:underline text-sm font-medium"
                        >
                          PO #{adjustment.purchase_order_id}
                        </Link>
                      ) : (
                        <span className="text-muted-foreground/40 text-sm">--</span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground max-w-[200px] truncate">
                      {adjustment.notes || '--'}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="icon-xs"
                          onClick={() => openEditDialog(adjustment)}
                        >
                          <Pencil className="size-3.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon-xs"
                          onClick={() => handleDelete(adjustment.id)}
                          className="text-destructive hover:text-destructive hover:bg-destructive/10"
                        >
                          <Trash2 className="size-3.5" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Pagination pagination={pagination} baseUrl="/adjustments" params={{ type: filters.type }} />

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingId ? 'Edit Adjustment' : 'Add Adjustment'}
            </DialogTitle>
            <DialogDescription>
              {editingId
                ? 'Update the adjustment details below.'
                : 'Enter the adjustment details below.'}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-3">
              <div className="space-y-2">
                <Label htmlFor="adj-type">Type</Label>
                <Select
                  value={form.adjustment_type}
                  onValueChange={(value: 'credit' | 'debit') =>
                    setForm({ ...form, adjustment_type: value })
                  }
                >
                  <SelectTrigger id="adj-type" className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="credit">Credit</SelectItem>
                    <SelectItem value="debit">Debit</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="adj-amount">Amount</Label>
                <Input
                  id="adj-amount"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  value={form.amount}
                  onChange={(e) => setForm({ ...form, amount: e.target.value })}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="adj-date">Date</Label>
                <Input
                  id="adj-date"
                  type="date"
                  value={form.date}
                  onChange={(e) => setForm({ ...form, date: e.target.value })}
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="adj-po">Link to Purchase Order (optional)</Label>
              <Select
                value={form.purchase_order_id || 'none'}
                onValueChange={(value) =>
                  setForm({ ...form, purchase_order_id: value === 'none' ? '' : value })
                }
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="No linked PO" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No linked PO</SelectItem>
                  {finalized_pos.map((po) => (
                    <SelectItem key={po.id} value={po.id.toString()}>
                      PO #{po.id} - {formatCurrency(parseFloat(po.amount))}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="adj-notes">Notes</Label>
              <Textarea
                id="adj-notes"
                placeholder="Add any relevant notes..."
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
              />
            </div>

            <DialogFooter>
              <Button type="submit" disabled={processing}>
                {processing
                  ? 'Saving...'
                  : editingId
                    ? 'Save Changes'
                    : 'Add Adjustment'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </AppLayout>
  )
}
